import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    general: {},
    contact: {},
    social: {},
    business: {},
    payment: {}
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('فشل في تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (group, key, value) => {
    setSettings(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Flatten settings for API
      const flatSettings = {};
      Object.values(settings).forEach(group => {
        Object.assign(flatSettings, group);
      });
      
      await api.put('/admin/settings', { settings: flatSettings });
      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('فشل في حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center">
              <Cog6ToothIcon className="w-8 h-8 text-gray-900 ml-3" />
              <h1 className="text-3xl font-bold text-gray-900">إعدادات المنصة</h1>
            </div>
            <p className="mt-2 text-gray-600">إدارة إعدادات المنصة العامة</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* General Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">الإعدادات العامة</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم الموقع
                  </label>
                  <input
                    type="text"
                    value={settings.general?.site_name || ''}
                    onChange={(e) => handleChange('general', 'site_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    وصف الموقع
                  </label>
                  <textarea
                    value={settings.general?.site_description || ''}
                    onChange={(e) => handleChange('general', 'site_description', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رابط الموقع
                  </label>
                  <input
                    type="url"
                    value={settings.general?.site_url || ''}
                    onChange={(e) => handleChange('general', 'site_url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Contact Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">معلومات الاتصال</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={settings.contact?.contact_email || ''}
                    onChange={(e) => handleChange('contact', 'contact_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رقم الهاتف
                  </label>
                  <input
                    type="tel"
                    value={settings.contact?.contact_phone || ''}
                    onChange={(e) => handleChange('contact', 'contact_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    العنوان
                  </label>
                  <input
                    type="text"
                    value={settings.contact?.contact_address || ''}
                    onChange={(e) => handleChange('contact', 'contact_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Social Media Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">روابط التواصل الاجتماعي</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    فيسبوك
                  </label>
                  <input
                    type="url"
                    value={settings.social?.social_facebook || ''}
                    onChange={(e) => handleChange('social', 'social_facebook', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://facebook.com/..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    انستغرام
                  </label>
                  <input
                    type="url"
                    value={settings.social?.social_instagram || ''}
                    onChange={(e) => handleChange('social', 'social_instagram', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://instagram.com/..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    إكس (تويتر)
                  </label>
                  <input
                    type="url"
                    value={settings.social?.social_twitter || ''}
                    onChange={(e) => handleChange('social', 'social_twitter', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://x.com/..."
                  />
                </div>
              </div>
            </div>

            {/* Business Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">الإعدادات التجارية</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نسبة العمولة (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.business?.commission_rate || ''}
                    onChange={(e) => handleChange('business', 'commission_rate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تكلفة الشحن (جنيه)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.business?.shipping_cost || ''}
                    onChange={(e) => handleChange('business', 'shipping_cost', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نسبة الضريبة (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.business?.tax_rate || ''}
                    onChange={(e) => handleChange('business', 'tax_rate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    العملة
                  </label>
                  <input
                    type="text"
                    value={settings.business?.currency || ''}
                    onChange={(e) => handleChange('business', 'currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Payment Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">معلومات الحساب البنكي</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم البنك
                  </label>
                  <input
                    type="text"
                    value={settings.payment?.bank_name || ''}
                    onChange={(e) => handleChange('payment', 'bank_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رقم الحساب
                  </label>
                  <input
                    type="text"
                    value={settings.payment?.bank_account_number || ''}
                    onChange={(e) => handleChange('payment', 'bank_account_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم صاحب الحساب
                  </label>
                  <input
                    type="text"
                    value={settings.payment?.bank_account_name || ''}
                    onChange={(e) => handleChange('payment', 'bank_account_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
